import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { representantesAPI } from '../../services/api';

const RepresentanteDetalle = () => {
  const { id } = useParams();
  const [representante, setRepresentante] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadRepresentante();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadRepresentante = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await representantesAPI.getById(id);
      setRepresentante(response.data);
    } catch (err) {
      setError('Error al cargar representante');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !representante) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
        {error || 'Representante no encontrado'}
        <Link to="/admin/representantes" className="ml-4 text-blue-600 hover:underline">← Volver</Link>
      </div>
    );
  }

  const tipoLabel = { padre: 'Padre', madre: 'Madre', otro: 'Otro' };
  const tipoColor = {
    padre: 'bg-blue-100 text-blue-700',
    madre: 'bg-pink-100 text-pink-700',
    otro: 'bg-gray-100 text-gray-700'
  };

  const InfoField = ({ label, value }) => (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="font-medium text-gray-800 text-sm sm:text-base">{value || '-'}</p>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Back button */}
      <Link to="/admin/representantes" className="inline-flex items-center text-blue-600 hover:underline text-sm">
        ← Volver a Representantes
      </Link>

      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className={`${representante.tipo === 'madre' ? 'bg-gradient-to-r from-pink-500 to-pink-600' : 'bg-gradient-to-r from-blue-500 to-blue-600'} p-4 sm:p-6`}>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-3xl sm:text-4xl">{representante.tipo === 'madre' ? '👩' : '👨'}</span>
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-white truncate">
                {representante.nombre} {representante.apellido}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="text-white text-opacity-80 text-sm">{representante.cedula}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-white bg-opacity-20 text-white`}>
                  {tipoLabel[representante.tipo] || representante.tipo}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="p-4 sm:p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Datos de Contacto</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <InfoField label="Teléfono" value={representante.telefono} />
            <InfoField label="Email" value={representante.email} />
            <InfoField label="Empresa" value={representante.empresa} />
            <InfoField label="Cargo" value={representante.cargo} />
            <InfoField label="Dirección de Trabajo" value={representante.direccion_trabajo} />
            <InfoField label="Teléfono Trabajo" value={representante.telefono_trabajo} />
          </div>
        </div>
      </div>

      {/* Jugadores Asociados */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">
          ⚽ Jugadores Asociados ({representante.jugadores?.length || 0})
        </h2>

        {!representante.jugadores || representante.jugadores.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No tiene jugadores asociados</p>
        ) : (
          <div className="space-y-3">
            {representante.jugadores.map((jugador) => (
              <Link
                key={jugador.id}
                to={`/admin/jugadores/${jugador.id}`}
                className="flex items-center justify-between p-3 sm:p-4 border rounded-lg hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">⚽</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 text-sm sm:text-base">
                      {jugador.nombres} {jugador.apellidos}
                    </p>
                    <div className="flex gap-2 text-xs text-gray-500">
                      <span>{jugador.cedula}</span>
                      <span>•</span>
                      <span>{jugador.edad} años</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${jugador.activo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {jugador.activo ? 'Activo' : 'Inactivo'}
                  </span>
                  <span className="text-blue-600 text-sm">→</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Account Info */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">🔐 Cuenta de Acceso</h2>
        {representante.usuario_id ? (
          <div className="bg-green-50 border border-green-300 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-green-600 font-bold">✓</span>
              <span className="font-medium text-green-800">Tiene cuenta de acceso</span>
            </div>
            <p className="text-sm text-green-700">Login: {representante.cedula}</p>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-yellow-600 font-bold">⚠️</span>
              <span className="font-medium text-yellow-800">Sin cuenta de acceso</span>
            </div>
            <p className="text-sm text-yellow-700">Este representante no tiene usuario creado en el sistema.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RepresentanteDetalle;
